import React, {useContext, useState} from 'react';
import {Box, Stack, Typography, Tab, Tabs, IconButton, Menu, MenuItem, CircularProgress, Tooltip, Skeleton} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {Copy01Icon, Delete01Icon, MoreVerticalIcon, PencilEdit01Icon, Refresh01Icon, Key01Icon, ArrowLeft01Icon} from '@hugeicons/core-free-icons';
import {Context} from '../../../../context/AppContext';
import {MCPToolsSectionNew} from './MCPToolsSectionNew';
import {MCPPromptsSectionNew} from './MCPPromptsSectionNew';
import {ConfirmModalNew} from './ConfirmModalNew';
import {EditMCPServerModalNew} from './EditMCPServerModalNew';
import EditTokenModalNew from './EditTokenModalNew';
import {selectedServerAtom, selectedTabAtom, serversAtom, mcpLoadingAtom, flowUidAtom, projectAtom, subProjectAtom} from '../atoms';
import {toast} from 'react-toastify';

export const MCPServerDetailsPanelNew = () => {
  const [selectedServer, setSelectedServer] = useAtom(selectedServerAtom);
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
  const [, setServers] = useAtom(serversAtom);
  const [loading] = useAtom(mcpLoadingAtom);
  const flowUid = useAtomValue(flowUidAtom);
  const project = useAtomValue(projectAtom);
  const subProject = useAtomValue(subProjectAtom);
  const mcpScope = {flowUid, projectId: project?.id, subProjectId: subProject?.id};
  const { deleteMCPServer, getAllServersWithDetails, updateMCPServer, reloadMCPServer, editServerToken } = useContext(Context);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [reloading, setReloading] = useState(false);

  if (!selectedServer) {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, px: 3, textAlign: 'center'}}>
        <Box>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: '#23264D'}}>No server selected</Typography>
          <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>Pick a server from the list to view its tools and prompts</Typography>
        </Box>
        <Stack
          direction="row" alignItems="center" gap={0.75} sx={{
            mt: 0.5, px: 1.5, py: 0.75, borderRadius: '999px', bgcolor: 'rgba(132, 145, 247, 0.08)', color: '#4B55C9',
            animation: 'mcp-point-left 1.4s ease-in-out infinite',
            '@keyframes mcp-point-left': {
              '0%, 100%': {transform: 'translateX(0)'},
              '50%': {transform: 'translateX(-5px)'},
            },
          }}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={15}/>
          <Typography sx={{fontSize: 12, fontWeight: 700}}>Choose a server from the list</Typography>
        </Stack>
      </Box>
    );
  }

  const toolCount = selectedServer?.tools?.length || 0;
  const promptCount = Object.keys(selectedServer?.prompts || {}).length;

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(selectedServer.endpoint);
      toast.success('Endpoint copied');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleDeleteServer = async () => {
    try {
      setDeleting(true);
      await deleteMCPServer(selectedServer.id);
      setSelectedServer(null);
      setConfirmOpen(false);
      if (flowUid) {
        await getAllServersWithDetails(mcpScope);
      }
    } catch (err) {
      toast.error('Failed to delete server');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSave = async (id, data) => {
    const updated = await updateMCPServer(id, data);
    if (updated) setSelectedServer(updated);
  };

  const handleTokenSave = async (newToken) => {
    try {
      await editServerToken(selectedServer.id, newToken);
      toast.success('Token updated');
    } catch (err) {
      toast.error('Failed to update token');
    }
  };

  const refreshSelectedServer = async () => {
    if (!flowUid) return;
    const refreshed = await getAllServersWithDetails(mcpScope);
    const updated = refreshed?.find(s => s.id === selectedServer.id);
    if (updated) setSelectedServer(updated);
  };

  const handleReload = async () => {
    setMenuAnchor(null);
    setReloading(true);
    try {
      await reloadMCPServer(selectedServer);
      await refreshSelectedServer();
    } catch (err) {
      console.error(err);
    } finally {
      setReloading(false);
    }
  };

  return (
    <Stack sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
      <Box sx={{p: 3}}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box sx={{flex: 1}}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                bgcolor: selectedServer.active ? '#2F9A6A' : '#DC2626',
              }}/>
              <Typography sx={{fontSize: 16, fontWeight: 700, color: '#23264D'}}>{selectedServer.name}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{mt: 0.75}}>
              <Typography sx={{fontSize: '12px', color: '#8A8AA0', fontFamily: 'monospace', wordBreak: 'break-all'}}>{selectedServer.endpoint}</Typography>
              <Tooltip title="Copy endpoint">
                <IconButton size="small" onClick={handleCopyEndpoint} sx={{p: 0.25, color: '#8A8AA0', '&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)', color: '#4B55C9'}}}>
                  <HugeiconsIcon icon={Copy01Icon} size={13} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" gap={1}>
            <Tooltip title="Reload tools &amp; prompts">
              <IconButton size="small" onClick={handleReload} disabled={reloading} sx={{'&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}}}>
                {reloading ? <CircularProgress size={16} sx={{color: '#8491F7'}}/> : <HugeiconsIcon icon={Refresh01Icon} size={17} />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{'&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}}}><HugeiconsIcon icon={MoreVerticalIcon} size={18} /></IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{sx: {borderRadius: '12px'}}}>
              <MenuItem onClick={() => {setMenuAnchor(null); setEditOpen(true);}} sx={{gap: 1}}><HugeiconsIcon icon={PencilEdit01Icon} size={16} /><Typography sx={{fontSize: '13px'}}>Edit Server</Typography></MenuItem>
              <MenuItem onClick={() => {setMenuAnchor(null); setTokenOpen(true);}} sx={{gap: 1}}><HugeiconsIcon icon={Key01Icon} size={16} /><Typography sx={{fontSize: '13px'}}>Update Token</Typography></MenuItem>
              <MenuItem onClick={handleCopyEndpoint} sx={{gap: 1}}><HugeiconsIcon icon={Copy01Icon} size={16} /><Typography sx={{fontSize: '13px'}}>Copy Endpoint</Typography></MenuItem>
              <MenuItem onClick={() => {setMenuAnchor(null); setConfirmOpen(true);}} sx={{color: '#DC2626', gap: 1}}><HugeiconsIcon icon={Delete01Icon} size={16} /><Typography sx={{fontSize: '13px'}}>Delete Server</Typography></MenuItem>
            </Menu>
          </Stack>
        </Stack>
      </Box>

      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{px: 3, '& .MuiTab-root': {textTransform: 'none', fontWeight: 700, fontSize: '13px', gap: 0.75, '&.Mui-selected': {color: '#8491F7'}}, '& .MuiTabs-indicator': {backgroundColor: '#8491F7'}}}>
        <Tab label={`Tools${toolCount > 0 ? ` (${toolCount})` : ''}`} value="tools" />
        <Tab label={`Prompts${promptCount > 0 ? ` (${promptCount})` : ''}`} value="prompts" />
      </Tabs>

      <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', p: 3}}>
        {loading ? (
          <Stack gap={1.25}>
            {Array.from({length: 5}).map((_, index) => (
              <Skeleton key={index} height={54} sx={{borderRadius: '12px'}}/>
            ))}
          </Stack>
        ) : selectedTab === 'tools' ? (<MCPToolsSectionNew server={selectedServer} onToolDeleted={refreshSelectedServer} />) : (<MCPPromptsSectionNew server={selectedServer} />)}
      </Box>

      <ConfirmModalNew
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteServer}
        title={`Delete "${selectedServer.name}"?`}
        description="This will permanently remove the server and its registered tools and prompts. This action cannot be undone."
        confirmText="Delete Server"
        loading={deleting}
      />

      <EditMCPServerModalNew
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleEditSave}
        serverData={selectedServer}
      />

      <EditTokenModalNew
        open={tokenOpen}
        onClose={() => setTokenOpen(false)}
        onSave={handleTokenSave}
        server={selectedServer}
      />
    </Stack>
  );
};
